<template>
    <nav class="app-navigation" :aria-label="$snippet('main_navigation', 'Main navigation')">
        <ul class="menu-list list-unstyled">
            <li
                v-for="item in navigationItems"
                :key="item.text"
                class="menu-item"
                v-test:menuItem
            >
                <b-link
                    v-if="item.textSegments"
                    v-bind="item.props"
                    class="nav-link"
                    :class="{
                        active: isDropdownActive(item),
                    }"
                    :tabindex="tabindex"
                    v-test:navLink
                >
                    <span
                        v-for="(segment, i) in item.textSegments"
                        :key="`${ segment.text }-${ i }`"
                        :class="segment.className"
                        v-test:segment
                    >{{ segment.text }}</span>
                </b-link>
                <template v-else>
                    <div class="menu-item-inner">
                        <b-link
                            v-bind="item.link"
                            class="nav-link nav-link-with-dropdown"
                            :class="{
                                'dropdown-open': isDropdownOpen(item.id),
                                active: isDropdownActive(item),
                            }"
                            :tabindex="tabindex"
                            v-test:navLink
                        >
                            <span
                                v-for="(segment, i) in item.name"
                                :key="`${ segment.text }-${ i }`"
                                :class="segment.className"
                                v-test:segment
                            >{{ segment.text }}</span>
                        </b-link>

                        <b-button
                            v-if="item.groupItems.length"
                            class="btn-nav-dropdown"
                            :class="{
                                'is-open': isDropdownOpen(item.id),
                            }"
                            :tabindex="tabindex"
                            variant="link"
                            @click="handleNavDropdownClick(item.id)"
                            v-test:btnNavDropdown
                        >
                            <app-icon
                                name="chevron_up"
                                class="nav-dropdown-indicator"
                                aria-hidden="true"
                            />
                        </b-button>
                    </div>

                    <b-collapse :id="item.id" :ref="`collapse-${ item.id }`">
                        <div class="collapse-inner">
                            <ul class="nav-child-list list-unstyled">
                                <li v-for="groupItem in item.groupItems" class="nav-child-list-item">
                                    <b-link
                                        v-bind="groupItem.props"
                                        class="nav-child-link"
                                        :class="{
                                            'child-active': isDropdownActive(groupItem),
                                        }"
                                        v-test:childLink
                                    >
                                        <span class="text-wrapper">
                                            <span
                                                v-for="(segment, i) in groupItem.textSegments"
                                                :key="`${ segment.text }-${ i }`"
                                                :class="segment.className"
                                                v-test:segment
                                            >{{ segment.text }}</span>
                                        </span>
                                    </b-link>
                                </li>
                            </ul>
                        </div>
                    </b-collapse>
                </template>
            </li>
        </ul>
    </nav>
</template>

<script>
    import { BLink, BCollapse, VBToggle } from 'bootstrap-vue';
    import { mapState } from 'vuex';

    export default {
        name: 'app-navigation',

        components: { BLink, BCollapse },

        directives: {
            'b-toggle': VBToggle,
        },

        data() {
            return {
                tabindex: 0,
                openDropdownId: null,
            };
        },

        computed: {
            ...mapState('navigation', ['headerNavigation', 'mobileMenuVisible']),

            ...mapState('locales', ['locale']),

            /**
             * Return the nav items with text segments to enable us
             * to highlight text in and including square brackets.
             *
             * @returns {{props: object, textSegments: {text: string, className: string}[]}[]}
             */
            navigationItems() {
                const navigationGroups = (
                    this.locale === this.headerNavigation.locale
                        ? this.headerNavigation.navigation_groups
                        : this.headerNavigation.localizations.find(({ locale }) => locale === this.locale)?.navigation_groups
                ) || this.headerNavigation.navigation_groups;

                return navigationGroups.map((group, i) => {
                    const groupItems = group.navigation_items.map((item) => {
                        const isExternal = item.external_link
                            && !item.external_link.startsWith('/');

                        const page = (
                            item.page?.locale === this.locale
                                ? item.page
                                : (item.page?.localizations ?? []).find(({ locale }) => locale === this.locale)
                        ) || item.page;

                        return {
                            textSegments: this.segmentText(item.text),
                            props: {
                                [isExternal ? 'href' : 'to']: item.external_link
                                    || page?.uri
                                    || '/',
                                target: isExternal
                                    ? '_blank'
                                    : null,
                                rel: isExternal
                                    ? 'noopener'
                                    : null,
                            },
                        };
                    });

                    // if only one item in the group, render the item only
                    if (groupItems.length === 1) {
                        return groupItems[0];
                    }

                    const isExternal = group.external_link
                        && !group.external_link.startsWith('/');

                    const page = (
                        group.page.locale === this.locale
                            ? group.page
                            : group.page.localizations.find(({ locale }) => locale === this.locale)
                    ) || group.page;

                    return {
                        id: `group-${ i }`,
                        name: this.segmentText(group.name),
                        link: {
                            [isExternal ? 'href' : 'to']: group.external_link
                                || page?.uri
                                || '/',
                            target: isExternal
                                ? '_blank'
                                : null,
                            rel: isExternal
                                ? 'noopener'
                                : null,
                        },
                        groupItems,
                    };
                }).filter((x) => x);
            },

            /**
             * Return an array of group ids based on the navigation items.
             *
             * @returns {string[]}
             */
            groupIds() {
                return this.navigationItems
                    .map(({ id }) => id)
                    .filter(Boolean);
            },
        },

        watch: {
            /**
             * Watch if menu opens so can update tabindex, and reset the open dropdown.
             *
             * @param {boolean} newValue
             */
            mobileMenuVisible(newValue) {
                if (newValue) {
                    this.tabindex = 0;

                    return;
                }

                this.$root.$emit('bv::toggle::collapse', this.openDropdownId);
                this.openDropdownId = null;
                this.tabindex = -1;
            },

            /**
             * Close all sub menus when the route changes.
             *
             */
            $route() {
                this.openDropdownId = null;

                this.groupIds.forEach((id) => {
                    const el = this.$refs[`collapse-${ id }`];

                    if (!el) {
                        return;
                    }

                    if (el[0].$el.classList.contains('show')) {
                        this.$root.$emit('bv::toggle::collapse', id);
                    }
                });
            },
        },

        mounted() {
            this.setTabindex(window.innerWidth);
        },

        methods: {
            /**
             * Determine tabindex value for links.
             *
             * @param {number} width
             */
            setTabindex(width) {
                if (!process.client) {
                    return;
                }

                if (width < 992 && !this.mobileMenuVisible) {
                    this.tabindex = -1;
                }
            },

            /**
             * Segment text so can highlight square brackets.
             *
             * @param {string} text
             * @returns {{text: string, className: string}[]}
             */
            segmentText(text) {
                if (!text) {
                    return [{
                        text,
                        className: '',
                    }];
                }

                return text.split(/(\[|\])/)
                    .filter((string) => string.trim())
                    .map((string) => ({
                        text: string,
                        className: /\[|]/g.test(string)
                            ? 'highlighted'
                            : '',
                    }));
            },

            /**
             * Check if the provided id is active.
             *
             * @param {string} id
             * @returns {boolean}
             */
            isDropdownOpen(id) {
                return this.openDropdownId === id;
            },

            /**
             * Check if the nav item or its children are active.
             *
             * @param {object} item
             * @returns {boolean}
             */
            isDropdownActive(item) {
                return this.$route.path === (item.props?.to || item.link?.to)
                    || (item.groupItems ?? []).some((item) => this.isDropdownActive(item));
            },

            /**
             * Handle nav dropdown click.
             *
             * @param {string} selectedId
             */
            handleNavDropdownClick(selectedId) {
                this.openDropdownId = this.openDropdownId === selectedId ? null : selectedId;

                this.groupIds.forEach((id) => {
                    if (id === selectedId) {
                        this.$root.$emit('bv::toggle::collapse', selectedId);

                        return;
                    }

                    const el = this.$refs[`collapse-${ id }`];

                    if (!el) {
                        return;
                    }

                    if (el[0].$el.classList.contains('show')) {
                        this.$root.$emit('bv::toggle::collapse', id);
                    }
                });

            },
        },
    };
</script>

<style lang="scss">
    @import "~bootstrap/scss/dropdown";
</style>

<style lang="scss" scoped>
    .app-navigation {
        width: 100%;
        background-color: secondary-palette(8);
        overflow-y: scroll;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 20px 40px 40px;
        overscroll-behavior: contain;
        @media (min-width: $menu-breakpoint) {
            padding: 0;
        }
    }

    .menu-list {
        @media (min-width: $menu-breakpoint) {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 50px;
            margin-bottom: 0;
        }
    }

    .menu-item {
        padding-top: 20px;
        padding-bottom: 20px;

        @media (min-width: $menu-breakpoint) {
            padding: 0;
        }

        &:not(:last-of-type) {
            border-bottom: 2px solid secondary-palette(7);

            @media (min-width: $menu-breakpoint) {
                border-bottom: 0;
            }
        }
    }

    .menu-item-inner {
        display: flex;
        align-items: center;
    }

    .nav-link {
        padding: 8px 0;
        color: $black;
        font-weight: bold;
        display: block;
        position: relative;

        &:hover,
        &:focus-visible,
        &:active {
            color: primary-palette(1);
            text-decoration: none;
            font-weight: bold;
        }

        &:after {
            position: absolute;
            left: 0;
            right: 0;
            height: 3px;
            content: '';
            background-color: $primary;
            border-radius: 2px;
            opacity: 0;
            bottom: 0;
            transition: transform 0.15s, opacity 0.15s;
            transform: translateY(3px);
        }

        &.active {
            &:after {
                transform: translateY(0);
                opacity: 1;
            }
        }

        &.nav-link-with-dropdown {
            display: inline-flex;

            &:after {
                right: -26px;
            }
        }

        &.nuxt-link-exact-active {
            @include media-breakpoint-down(md) {
                color: primary-palette(1);
            }
        }
    }

    .highlighted {
        color: primary-palette(1);
        font-weight: 400;
    }

    .btn-nav-dropdown {
        padding: 10px 0 11px 10px;
        color: $black;
        font-weight: bold;
        display: inline-flex;
        border: 0;
        border-radius: 0;
        transition: none;

        @media (min-width: $menu-breakpoint) {
            padding: 30px 10px;
            margin-right: -10px;
        }

        &:focus {
            color: $primary;
            text-decoration: none;
        }

        &:hover,
        &:focus-visible,
        &:active {
            color: $primary;
            text-decoration: none;

            > span::after {
                border-bottom-color: primary-palette(1);
            }
        }

        > span {
            position: relative;

            &::after {
                @media (min-width: $menu-breakpoint) {
                    border-bottom: 2px solid transparent;
                    position: absolute;
                    bottom: -5px;
                    left: 0;
                    right: 0;
                    content: '';
                    transition: border-color 0.3s;
                }
            }
        }

        &.is-open {
            .nav-dropdown-indicator {
                transform: rotate(0);
                color: primary-palette(1);
            }

            > span::after {
                border-bottom-color: primary-palette(1);
            }
        }
    }

    .nav-dropdown-indicator {
        transform: rotate(180deg);
        transition: transform 0.3s ease;
        margin-left: auto;
    }

    .collapse,
    .collapsing {
        @media (min-width: $menu-breakpoint) {
            position: absolute;
            top: 80px;
            left: 0;
            right: 0;
        }
    }

    .collapse-inner {
        @media (min-width: $menu-breakpoint) {
            background-color: $white;
            padding-top: 22px;
            padding-bottom: 22px;
            border-top: 2px solid secondary-palette(7);
            text-align: center;
        }
    }

    .nav-child-list {
        padding-bottom: 10px;

        @media (min-width: $menu-breakpoint) {
            display: inline-flex;
            justify-content: center;
            padding-bottom: 0;
        }
    }

    .nav-child-list-item {
        @media (min-width: $menu-breakpoint) {
            flex: 1 0 0;
            text-align: center;
            white-space: nowrap;
            padding-left: 20px;
            padding-right: 20px;
        }
    }

    .nav-child-link {
        padding: 10px 0;
        color: $black;
        font-weight: 250;
        display: block;

        @media (min-width: $menu-breakpoint) {
            padding: 0 10px;
        }

        &:hover,
        &:focus-visible,
        &:active {
            color: primary-palette(1);
            text-decoration: none;
        }

        &.nuxt-link-exact-active:not(:hover):not(:active):not(:focus-visible) {
            color: inherit;
        }
    }

    .child-active {
        .text-wrapper {
            position: relative;

            &:after {
                position: absolute;
                bottom: -5px;
                left: 0;
                right: 0;
                height: 3px;
                content: '';
                background-color: $primary;
                border-radius: 2px;
            }
        }
    }
</style>
